import apiClient from "../utils/apiClient";
import { UserFacingError } from "../utils/userFacingError";
import { GLOBALSETTINGS } from "../data/constants";
import { PIF_PDF_MESSAGES } from "../constants/messages";
import { POSTIF_RESULT_CODE, readPostIfWrite } from "./pifWriteResult";
import {
  CreatePostIfDraftRequestDto,
  PostIfApprovalChainStatusDto,
  PostIfAttributeValueDto,
  PostIfCanActResultDto,
  PostIfCanHireResultDto,
  PostIfDto,
  PostIfEmploymentHistoryDto,
  PostIfIdentityLookupRequestDto,
  PostIfIdentityLookupResultDto,
  PostIfLookupItemDto,
  PostIfPositionRulesDto,
  PostIfResultDto,
  SavePostIfAnswersRequestDto,
  SavePostIfApplicantRequestDto,
} from "../types/pifDto";

// ---------------------------------------------------------------------------
// PostIF API (api/postif/*) — Epic 79818 reference data + identity lookup,
// Epic 79819 draft persistence. Routes are kebab-case and do not match the C#
// action names; see modules/Portal/API_ENDPOINTS.md in the Enterprise API repo.
// ---------------------------------------------------------------------------

/**
 * Matches an SSN or Employee ID against the enterprise employee records.
 *
 * A POST on purpose: the raw identifier must never reach a URL or a server log.
 * Throws on 429 (past the per-user-per-minute limit) and 403 (caller may not hire) —
 * callers going through useApi get the central alert and a null result.
 */
export const postIfIdentityLookup = async (
  request: PostIfIdentityLookupRequestDto,
): Promise<PostIfIdentityLookupResultDto> => {
  const response = await apiClient.post<PostIfIdentityLookupResultDto>("/postif/identity-lookup", request);
  return response.data;
};

/**
 * Creates the PostIF row. Called on the FIRST SAVE, not when the form opens.
 *
 * It used to run as soon as an Employee ID resolved, on the stated grounds that it "places the
 * soft record-lock" — it does not: create-draft is the one PostIF write with no AuthorizeAndLock,
 * and the lock is taken by save-applicant/save-answers/submit. Creating here left an empty row
 * behind every time a supervisor looked someone up and walked away (Bug 79824).
 */
export const createPostIfDraft = (request: CreatePostIfDraftRequestDto): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/create-draft", request));

/**
 * The EAV field catalogue with no answers — what the dynamic section renders from before any
 * PostIF exists. GET /postif/{id} returns the same shape with a record's answers filled in.
 */
export const getPostIfAttributeCatalogue = async (): Promise<PostIfAttributeValueDto[]> => {
  const response = await apiClient.get<PostIfAttributeValueDto[]>("/postif/attributes");
  return response.data ?? [];
};

/**
 * The draft's header, applicant and full answer catalogue. The only source of each
 * field's `postIfAttributeId`, which save-answers writes against.
 */
export const getPostIf = async (postIfId: number): Promise<PostIfDto> => {
  const response = await apiClient.get<PostIfDto>(`/postif/${postIfId}`);
  return response.data;
};

/**
 * The PostIF as a branded, server-generated PDF (API Epic 83469).
 *
 * 404 means not found OR not allowed — the server deliberately doesn't say which. Any other HTTP
 * failure gets the generic PDF message; a network failure or timeout is already a UserFacingError
 * from apiClient (it has no `response`) and goes through unchanged.
 */
export const downloadPostIfPdf = async (postIfId: number): Promise<Blob> => {
  try {
    const response = await apiClient.get<Blob>(`/postif/${postIfId}/pdf`, {
      responseType: "blob",
      // Building the PDF can take a few seconds; the default API timeout is too tight.
      timeout: GLOBALSETTINGS.DOWNLOAD_TIMEOUT_MS,
    });
    return response.data;
  } catch (error) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status === 404) throw new UserFacingError(PIF_PDF_MESSAGES.NOT_AVAILABLE);
    if (status) throw new UserFacingError(PIF_PDF_MESSAGES.FAILED);
    throw error;
  }
};

/** Upserts the applicant identity. Not used for the existing-active-employee flow. */
export const savePostIfApplicant = (request: SavePostIfApplicantRequestDto): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/save-applicant", request));

/** Upserts EAV answers. Returns 422 with `rejected` when a value fails its definition. */
export const savePostIfAnswers = (request: SavePostIfAnswersRequestDto): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/save-answers", request), [
    POSTIF_RESULT_CODE.VALIDATION_FAILED,
  ]);

/**
 * Signs and submits. Records name/date/time/IP, locks the form, releases the record-lock
 * and hands off to the approval chain. Returns 422 with `missing` when incomplete.
 */
export const submitPostIf = (postIfId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/submit", { postIfId }), [
    POSTIF_RESULT_CODE.VALIDATION_FAILED,
    POSTIF_RESULT_CODE.MISSING_REQUIRED,
  ]);

/** Soft-deletes a draft. A submitted PostIF can never be deleted. */
export const softDeletePostIf = (postIfId: number): Promise<PostIfResultDto> =>
  readPostIfWrite(() => apiClient.post<PostIfResultDto>("/postif/soft-delete", { postIfId }));

/** Whether the caller may start a hire at all, and into which positions. */
export const getPostIfCanHire = async (): Promise<PostIfCanHireResultDto> => {
  const response = await apiClient.get<PostIfCanHireResultDto>("/postif/can-hire");
  return response.data;
};

/** Whether the caller may act on a PostIF. Omit `postIfId` for a scope-level check. */
export const getPostIfCanAct = async (params: { postIfId?: number; action: string }): Promise<PostIfCanActResultDto> => {
  const query = new URLSearchParams({ action: params.action });
  if (params.postIfId) query.append("postIfId", String(params.postIfId));
  const response = await apiClient.get<PostIfCanActResultDto>(`/postif/can-act?${query.toString()}`);
  return response.data;
};

/**
 * Whether the caller's Reports-To hierarchy resolves a full approval chain — the same resolution
 * submit runs, asked before the form is filled in rather than after (Bug 83231).
 *
 * Resolves null on a 404 so an API that doesn't have the route yet opens the form exactly as it
 * did before, without an alert; submit still refuses an unresolvable chain with -7 either way.
 */
export const getPostIfApprovalChainStatus = async (): Promise<PostIfApprovalChainStatusDto | null> => {
  try {
    const response = await apiClient.get<PostIfApprovalChainStatusDto>("/postif/approval-chain-status");
    return response.data;
  } catch (error) {
    if ((error as { response?: { status?: number } } | null)?.response?.status === 404) return null;
    throw error;
  }
};

export const getPostIfDepartments = async (): Promise<PostIfLookupItemDto[]> => {
  const response = await apiClient.get<PostIfLookupItemDto[]>("/postif/departments");
  return response.data;
};

export const getPostIfPositions = async (departmentId: number): Promise<PostIfLookupItemDto[]> => {
  const response = await apiClient.get<PostIfLookupItemDto[]>(`/postif/positions?departmentId=${departmentId}`);
  return response.data;
};

/**
 * Report To candidates. Each `id` is a System_Users.UserId. Without a departmentId the API returns
 * every active Enterprise login - the PostIF form's first "Report To" field (Epic 82836, no
 * narrowing in v1). With one, the department's active-position holders - the workflow Run screen.
 */
export const getPostIfReportToUsers = async (departmentId?: number): Promise<PostIfLookupItemDto[]> => {
  const url = departmentId ? `/postif/report-to-users?departmentId=${departmentId}` : "/postif/report-to-users";
  const response = await apiClient.get<PostIfLookupItemDto[]>(url);
  return response.data;
};

export const getPostIfSiteLocations = async (): Promise<PostIfLookupItemDto[]> => {
  const response = await apiClient.get<PostIfLookupItemDto[]>("/postif/site-locations");
  return response.data;
};

export const getPostIfServices = async (departmentId: number): Promise<PostIfLookupItemDto[]> => {
  const response = await apiClient.get<PostIfLookupItemDto[]>(`/postif/services?departmentId=${departmentId}`);
  return response.data;
};

export const getPostIfClients = async (serviceId: number): Promise<PostIfLookupItemDto[]> => {
  const response = await apiClient.get<PostIfLookupItemDto[]>(`/postif/clients?serviceId=${serviceId}`);
  return response.data;
};

/** Rate types, plus a synthetic `{id:-1, text:'Acuity-based'}` the API appends. */
export const getPostIfRateTypes = async (): Promise<PostIfLookupItemDto[]> => {
  const response = await apiClient.get<PostIfLookupItemDto[]>("/postif/rate-types");
  return response.data;
};

/** Show/hide/lock rules and the pay bands for a department + position pair. */
export const getPostIfPositionRules = async (params: {
  departmentId: number;
  positionId: number;
}): Promise<PostIfPositionRulesDto> => {
  const response = await apiClient.get<PostIfPositionRulesDto>(
    `/postif/position-rules?departmentId=${params.departmentId}&positionId=${params.positionId}`,
  );
  return response.data;
};

/** Enterprise employment history. Empty for a brand-new applicant. */
export const getPostIfEmploymentHistory = async (
  enterpriseEmployeeId: number,
): Promise<PostIfEmploymentHistoryDto[]> => {
  const response = await apiClient.get<PostIfEmploymentHistoryDto[]>(
    `/postif/employment-history?enterpriseEmployeeId=${enterpriseEmployeeId}`,
  );
  return response.data;
};

/* ── Epic 79822: real PostIF list + approver-review service.
   Implementations live in pifListApproval.ts (kept separate so Epic 79820's form DTOs in this
   file and Epic 79822's list/approval DTOs don't collide). These replace 79820's stub versions. */
export {
  APPROVAL_ROLES,
  assemblePifApprovalRecord,
  getPifApproval,
  PIF_APPROVAL_NOT_RECIPIENT,
  getMyPostInterviewForms,
  getPostInterviewForms,
  markPostIfAsEmployee,
  approvePifById,
  rejectPifById,
  resendPifApprovalLink,
  emailHrDirectorApprovalRequest,
  emailHrDoNotRehireContact,
} from "./pifListApproval";
